/**
 * Authentication Service
 * 
 * Handles user authentication with Supabase and ThingsBoard integration
 * 
 * @module services/auth
 */

import { supabase } from '../supabase/client';

export interface RegisterParams {
  email: string;
  password: string;
  fullName: string;
}

export interface LoginParams {
  email: string;
  password: string;
}

export interface AuthError {
  message: string;
  /** GoTrue error code (e.g. 'invalid_credentials'), when available. */
  code?: string;
  /** HTTP status code, when available. */
  status?: number;
}

/**
 * Register a new user
 * Creates Supabase account, profile, and ThingsBoard customer/user
 * 
 * @param params - Registration parameters
 * @returns Success or error
 */
export async function register(params: RegisterParams): Promise<{
  success: boolean;
  error?: AuthError;
}> {
  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      console.error('Failed to fetch session during registration:', sessionError);
    }

    const session = sessionData.session;

    if (!session || !session.user) {
      return {
        success: false,
        error: {
          message: 'Please verify your email before creating an account.',
        },
      };
    }

    const { user: sessionUser, access_token: accessToken } = session;
    const sessionEmail = sessionUser.email?.toLowerCase();
    if (!sessionEmail || sessionEmail !== params.email.toLowerCase()) {
      return {
        success: false,
        error: {
          message: 'Verified email does not match the email entered. Please verify again.',
        },
      };
    }

    // Step 1: Update the authenticated user with password and metadata
    const { data: updatedUserData, error: updateError } = await supabase.auth.updateUser(
      {
        password: params.password,
        data: {
          display_name: params.fullName,
          full_name: params.fullName,
        },
      },
    );

    if (updateError) {
      return {
        success: false,
        error: {
          message: updateError.message,
          code: updateError.code,
          status: updateError.status,
        },
      };
    }

    const user = updatedUserData?.user ?? sessionUser;

    if (!user) {
      return {
        success: false,
        error: {
          message: 'Failed to finalize user account.',
        },
      };
    }

    // Step 2: Profile is automatically created by database trigger
    // Wait a moment to ensure trigger has executed
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 500);
    });

    // Step 3: Create ThingsBoard customer and user (REQUIRED)
    // If this fails, we rollback the Supabase user to maintain data consistency
    try {
      // Skip ThingsBoard creation if profile already has IDs (idempotency)
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('thingsboard_customer_id, thingsboard_user_id')
          .eq('id', user.id)
          .maybeSingle() as { data: { thingsboard_customer_id?: string | null; thingsboard_user_id?: string | null } | null };
        if (profile?.thingsboard_customer_id) {
          console.log('ThingsBoard already set up for user, skipping creation');
          return { success: true };
        }
      } catch (profileError) {
        console.warn('Failed to load ThingsBoard profile data:', profileError);
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/thingsboard-user-management`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            action: 'createCustomerAndUser',
            params: {
              userId: user.id,
              email: params.email,
              fullName: params.fullName,
            },
          }),
        }
      );

      // Treat 409 (Already Exists) from the Edge Function as a non-fatal, idempotent success
      // so that retrying registration does not roll back an otherwise valid account setup
      if (!response.ok && response.status !== 409) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create ThingsBoard account');
      }

      const result = response.status === 409 ? await response.json().catch(() => ({})) : await response.json();
      console.log('ThingsBoard account created:', result);
    } catch (tbError) {
      console.error('ThingsBoard creation error:', tbError);

      // IMPORTANT: Rollback Supabase user to maintain data consistency
      // Delete the user since ThingsBoard account creation failed
      try {
        // Use database RPC function to delete orphaned user
        const { error: deleteError } = await supabase.rpc('delete_orphaned_user', {
          user_id: user.id
        });

        if (deleteError) {
          console.error('Failed to rollback user creation:', deleteError);
          // Even if rollback fails, we still need to sign out and report error
        } else {
          console.log('Successfully rolled back user creation');
        }
      } catch (rollbackError) {
        console.error('Rollback error:', rollbackError);
      }

      // Sign out any session
      await supabase.auth.signOut();

      return {
        success: false,
        error: {
          message: tbError instanceof Error
            ? `Registration failed: ${tbError.message}`
            : 'Failed to create complete account. Please try again or contact support.',
        },
      };
    }

    return { success: true };
    
  } catch (error) {
    console.error('Registration error:', error);
    return {
      success: false,
      error: {
        message: error instanceof Error
          ? error.message
          : 'An unexpected error occurred during registration',
      },
    };
  }
}

/**
 * Login user
 * Authenticates with Supabase
 * 
 * @param params - Login parameters
 * @returns Success or error
 */
export async function login(params: LoginParams): Promise<{ 
  success: boolean; 
  error?: AuthError 
}> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: params.email,
      password: params.password,
    });

    if (error) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
          status: error.status,
        },
      };
    }

    if (!data.user) {
      return {
        success: false,
        error: {
          message: 'Login failed',
        },
      };
    }

    // Verify ThingsBoard account exists  
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('thingsboard_customer_id, thingsboard_user_id, role')
        .eq('id', data.user.id)
        .maybeSingle() as { data: { thingsboard_customer_id?: string | null; thingsboard_user_id?: string | null; role?: 'admin' | 'technician' | 'viewer' } | null };

      if (profile && !profile.thingsboard_customer_id) {
        console.warn('ThingsBoard account not set up for user');
        // Continue anyway - might be an older account
      }

      // Service-provider approval is checked after login (isServiceProviderApproved);
      // since #3556 an admin's approval is what grants the technician role.
    } catch (profileError) {
      console.error('Profile/applications check error:', profileError);
      // If we cannot verify, allow login to avoid locking legit users
    }

    return { success: true };
    
  } catch (error) {
    console.error('Login error:', error);
    return {
      success: false,
      error: {
        message: error instanceof Error 
          ? error.message 
          : 'An unexpected error occurred during login',
      },
    };
  }
}

/**
 * Logout user
 * Signs out ONLY the current session/device (scope: 'local') and clears client
 * storage. Using 'global' here revoked every session for the account across all
 * devices, so two people sharing an account dropped each other mid-session (#2907).
 */
export async function logout(): Promise<void> {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' });

    if (error) {
      console.error('Logout failed', error);
      throw error;
    }
  } finally {
    safeClearStorage();
    clearProfileRoleCache();
  }
}

function safeClearStorage() {
  try {
    window.localStorage.clear();
  } catch (error) {
    console.warn('Failed to clear localStorage on logout', error);
  }

  try {
    window.sessionStorage.clear();
  } catch (error) {
    console.warn('Failed to clear sessionStorage on logout', error);
  }
}

/**
 * Get current user
 */
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();

  if (error) {
    console.error('Error fetching current user:', error);
    return null;
  }

  return user;
}

/**
 * Current user's id WITHOUT a network round trip.
 *
 * `supabase.auth.getUser()` hits GoTrue (/auth/v1/user) on every call to
 * re-validate the token server-side; `getSession()` reads the already-stored
 * session (auto-refreshing only when the access token is actually expired).
 * For a data read that is then RLS-scoped by auth.uid() on the server, the
 * extra validation buys nothing — the real query rejects a bad token anyway —
 * so this is the right primitive when you only need the id to filter/insert.
 *
 * Keep getCurrentUser()/getUser() for the auth flow itself or immediately
 * before a sensitive action, where fresh server validation matters.
 *
 * Returns null when there is no session.
 */
export async function getCurrentUserId(): Promise<string | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error reading session for user id:', error);
    return null;
  }
  return data.session?.user?.id ?? null;
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
}

/**
 * Update the current user's role in profiles
 * Maps business roles to allowed values:
 * - homeowner -> 'viewer'
 * - service-provider -> 'technician'
 */
export async function setProfileRole(businessRole: 'homeowner' | 'service-provider'): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // #3556: the technician role is granted only by an admin's approval
  if (businessRole === 'service-provider') {
    throw new Error('Service providers are approved by an admin');
  }
  const newRole = 'viewer';

  const { error } = await supabase
    .from('profiles')
    .update({ role: newRole })
    .eq('id', user.id);

  if (error) {
    throw new Error(error.message);
  }
}

/** Get the current user's profile role (cached for 5 minutes — #2071) */
let _cachedRole: 'admin' | 'technician' | 'viewer' | 'employee' | 'member' | null = null;
let _cachedRoleExpiry = 0;
let _cachedRoleUserId: string | null = null;
let _pendingRolePromise: Promise<'admin' | 'technician' | 'viewer' | 'employee' | 'member' | null> | null = null;

/** Forget the cached role, e.g. right after an admin approved this user (#3556) */
export function clearProfileRoleCache(): void {
  _cachedRole = null;
  _cachedRoleExpiry = 0;
  _cachedRoleUserId = null;
  _pendingRolePromise = null;
}

export async function getProfileRole(): Promise<'admin' | 'technician' | 'viewer' | 'employee' | 'member' | null> {
  // Use local session (no API call) to check user ID for cache
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) {
    _cachedRole = null;
    _cachedRoleUserId = null;
    return null;
  }

  // Return cached result if still valid and same user
  if (_cachedRole !== null && _cachedRoleUserId === userId && Date.now() < _cachedRoleExpiry) {
    return _cachedRole;
  }

  // Deduplicate concurrent calls — if a fetch is already in flight, reuse it
  if (_pendingRolePromise) {
    return _pendingRolePromise;
  }

  _pendingRolePromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        console.error('[getProfileRole] Failed to fetch profile role:', error);
        return null;
      }
      const role = (data as { role: 'admin' | 'technician' | 'viewer' | 'employee' | 'member' } | null)?.role ?? null;
      _cachedRole = role;
      _cachedRoleUserId = userId;
      _cachedRoleExpiry = Date.now() + 5 * 60 * 1000; // Cache 5 minutes
      return role;
    } finally {
      _pendingRolePromise = null;
    }
  })();

  return _pendingRolePromise;
}

/**
 * Check if service provider is approved
 * Returns true if the user is an approved service provider
 */
export async function isServiceProviderApproved(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  // Check if user is a technician
  const { data: profile } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', user.id)
    .maybeSingle();

  if (profile?.role !== 'technician') {
    return true; // Non-technicians are always approved
  }

  // Query user's own application (RLS allows this)
  const { data: application, error } = await supabase
    .from('service_provider_applications')
    .select('status')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error checking service provider approval:', error);
    return false;
  }

  return application?.status === 'approved';
}

/**
 * #3556: applicants stay 'viewer' until an admin approves them. Returns true
 * only on POSITIVE proof that this user is such an applicant: signed in, role
 * 'viewer', latest application 'submitted', and no house owned or shared as a
 * member. Any error or doubt returns false, so homeowners are never sent to the
 * approval-pending page by a network hiccup.
 */
export async function isAwaitingProviderApproval(): Promise<boolean> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) return false;

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .maybeSingle();
    if (profileError || profile?.role !== 'viewer') return false;

    const { data: application, error: applicationError } = await supabase
      .from('service_provider_applications')
      .select('status')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (applicationError || application?.status !== 'submitted') return false;

    const [houses, memberships] = await Promise.all([
      supabase.from('home_owner_objects').select('id', { count: 'exact', head: true }).eq('owner_id', user.id),
      supabase.from('homeowner_member_property_assignments').select('id', { count: 'exact', head: true }).eq('member_user_id', user.id),
    ]);
    if (houses.error || memberships.error) return false;

    return houses.count === 0 && memberships.count === 0; // null count = not proven
  } catch {
    return false;
  }
}
